import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ChevronDown, FolderOpen } from "lucide-react";
import LocalizedLink from "@/components/LocalizedLink";
import FlagIcon from "@/components/flags/FlagIcon";
import LanguageSwitcher from "@/components/catalog/LanguageSwitcher";

import { useLocalizedPath } from "@/hooks/useLocalizedPath";



const langOptions = [
  { code: "de", label: "Deutsch" },
  { code: "en", label: "English" },
  { code: "fr", label: "Français" },
  { code: "it", label: "Italiano" },
  { code: "pt", label: "Português" },
  { code: "es", label: "Español" },
];

const SiteHeader: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const lp = useLocalizedPath();
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const pendingNavRef = React.useRef<string | null>(null);

  const handleMobileNav = (path: string) => {
    pendingNavRef.current = path;
    setShowMobileMenu(false);
  };

  // Close mobile menu on route change
  React.useEffect(() => {
    setShowMobileMenu(false);
  }, [location.pathname]);

  const navLinks = [
    { label: t("nav_horses"), to: "/horses" },
    { label: t("blog"), to: "/blog" },
    { label: t("nav_about"), to: "/about" },
    { label: t("nav_faq"), to: "/faq" },
    { label: t("nav_info"), to: "/info" },
    { label: t("nav_contact"), to: "/contact" },
  ];

  return (
    <>
      {/* Mobile menu overlay */}
      <AnimatePresence onExitComplete={() => {
        if (pendingNavRef.current) {
          navigate(pendingNavRef.current);
          pendingNavRef.current = null;
        }
      }}>
        {showMobileMenu && (
          <>
            <motion.div key="overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 bg-foreground/60 backdrop-blur-sm" onClick={() => setShowMobileMenu(false)} />
            <motion.div key="drawer" initial={{ x: "-100%" }} animate={{ x: 0 }} exit={{ x: "-100%" }}
              transition={{ type: "spring", damping: 30, stiffness: 300 }}
              className="fixed inset-y-0 left-0 z-[51] w-[85%] max-w-sm bg-background shadow-2xl flex flex-col">
              <div className="flex items-center justify-between px-6 py-5 border-b border-border">
                <span className="text-sm font-bold text-foreground tracking-tight">HORSEVAULT®</span>
                <button onClick={() => setShowMobileMenu(false)} aria-label={t("close")} className="p-2 rounded-full bg-muted">
                  <X className="w-5 h-5 text-foreground" />
                </button>
              </div>
              <nav className="flex-1 px-4 py-6 space-y-1">
                {navLinks.map(link => {
                  const lang = i18n.language || "de";
                  const fullPath = `/${lang}${link.to}`;
                  return (
                    <button key={link.to}
                      onClick={() => handleMobileNav(fullPath)}
                      className="block w-full text-left px-4 py-3 text-sm text-foreground hover:bg-muted rounded-lg transition-colors">
                      {link.label}
                    </button>
                  );
                })}
              </nav>
              <div className="px-4 py-5 border-t border-border space-y-3">
                <button
                  onClick={() => handleMobileNav(`/${i18n.language || "de"}/seller`)}
                  className="flex items-center justify-center gap-2 w-full bg-primary text-primary-foreground px-5 py-3 rounded-lg text-sm font-semibold">
                  <FolderOpen className="w-4 h-4" /> {t("seller_cta_nav")}
                </button>
                <div className="flex items-center justify-between">
                  <LanguageSwitcher variant="catalog" />
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Header bar */}
      <header className="bg-background border-b border-border sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => setShowMobileMenu(true)} aria-label={t("menu")} className="lg:hidden text-foreground p-2 -ml-1 min-w-[44px] min-h-[44px] flex items-center justify-center">
              <Menu className="w-5 h-5" />
            </button>
            <LocalizedLink to="/" className="flex items-center gap-2">
              <img src="/images/logo-horsevault.png" alt="HorseVault® – logo" className="h-20 w-20 object-contain" width={80} height={80} />
              <span className="text-lg font-bold tracking-tight text-foreground hidden sm:block">HorseVault®</span>
            </LocalizedLink>
          </div>

          <nav className="hidden lg:flex items-center gap-8">
            {navLinks.map(link => (
              <LocalizedLink key={link.to} to={link.to}
                className="text-sm text-muted-foreground hover:text-foreground transition-colors font-medium">
                {link.label}
              </LocalizedLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            {/* Language */}
            <div className="relative group">
              <button aria-label={t("change_language")} className="flex items-center gap-1.5 h-8 px-2 text-foreground/70 hover:text-foreground transition-colors">
                <FlagIcon code={i18n.language || "de"} className="w-5 h-4" />
                <span className="text-xs font-semibold uppercase text-foreground">{(i18n.language || "de").toUpperCase()}</span>
                <ChevronDown className="w-3 h-3 opacity-60" />
              </button>
              <div className="absolute right-0 top-full mt-1 hidden group-hover:flex flex-col bg-background border border-border rounded-lg shadow-xl py-1.5 min-w-[140px] z-50">
                {langOptions.map(l => (
                  <button key={l.code}
                    onClick={() => {
                      i18n.changeLanguage(l.code);
                      const newPath = location.pathname.replace(/^\/[a-z]{2}/, `/${l.code}`) + location.search;
                      navigate(newPath, { replace: true });
                    }}
                    className={`flex items-center gap-2.5 px-3 py-2 text-sm text-left transition-colors ${
                      i18n.language === l.code ? "text-foreground font-semibold bg-muted" : "text-muted-foreground hover:text-foreground hover:bg-muted"
                    }`}>
                    <FlagIcon code={l.code} className="w-5 h-4" />
                    <span>{l.label}</span>
                  </button>
                ))}
              </div>
            </div>
            
            <LocalizedLink to="/seller"
              className="hidden sm:inline-flex items-center gap-2 bg-primary text-primary-foreground px-5 py-2 rounded-lg text-sm font-semibold hover:opacity-90 transition-opacity">
              <FolderOpen className="w-4 h-4" />
              {t("seller_cta_nav")}
            </LocalizedLink>
          </div>
        </div>
      </header>

      {/* Dossier info bar — premium */}
      <div className="relative overflow-hidden border-b border-primary/10" style={{ background: "linear-gradient(90deg, hsl(var(--primary) / 0.06), hsl(var(--primary) / 0.12), hsl(var(--primary) / 0.06))" }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-2 sm:py-2.5 flex items-center justify-center gap-2.5">
          <div className="flex items-center justify-center w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-primary/10 shrink-0">
            <FolderOpen className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-primary" />
          </div>
          <span className="text-[11px] sm:text-xs font-medium tracking-wide text-primary/90 text-center leading-snug">
            {t("header_dossier_banner")}
          </span>
        </div>
      </div>
    </>
  );
};

export default SiteHeader;
